import { useMemo, useState } from 'react';
import { Alert, Button, Modal, Progress, Radio, Select, Space, Typography, notification } from 'antd';
import { FilePdfOutlined, PaperClipOutlined, PrinterOutlined } from '@ant-design/icons';
import type { FieldMeta, TableContext } from '../../api/types';
import type { ContractTemplate, ContractRenderData } from '../../contract/types';
import { getRecordIds, getTableName, pickAttachmentField, rememberAttachmentField, saveFileToAttachmentCell } from '../../api/bitable';
import { fetchContractData } from '../../contract/dataFetcher';
import { buildBatchHtml, buildContractHtml } from '../../contract/renderer';
import { resolveFileNameTemplate } from '../../contract/fileName';
import { renderHtmlOffscreen } from '../../utils/renderOffscreen';
import { printHtmlViaIframe } from '../../utils/printHtml';
import { buildPdfFileName, exportManyToPdf } from '../../exporters/pdfExporter';
import { buildAttachmentFile, type AttachmentFormat } from '../../exporters/attachmentExporter';

interface Props {
  open: boolean;
  onClose: () => void;
  context: TableContext;
  template: ContractTemplate;
  fieldMetas: FieldMeta[];
  /** 计费闸门（返回 false 表示未通过，不继续）。 */
  gate: () => Promise<boolean>;
}

type Range = 'visible' | 'selected' | 'all';

const PAPER_W_MM: Record<string, [number, number]> = {
  A4: [210, 297],
  A5: [148, 210],
  A3: [297, 420],
  Letter: [216, 279],
};
function paperWidthMm(s: { paperSize: string; orientation: string }): number {
  const [w, h] = PAPER_W_MM[s.paperSize] ?? PAPER_W_MM.A4;
  return s.orientation === 'portrait' ? w : h;
}
const MAX_BATCH = 100;
/** 飞书附件临时链接只活几分钟；逐条存附件可能跑上好几分钟，取数超过这个时长就重取一次。 */
const ATTACHMENT_TTL_MS = 3 * 60 * 1000;

export function BatchPrintModal({ open, onClose, context, template, fieldMetas, gate }: Props) {
  const [range, setRange] = useState<Range>('visible');
  const [busy, setBusy] = useState<'print' | 'pdf' | 'attach' | null>(null);
  const [progress, setProgress] = useState('');
  const [pct, setPct] = useState<number | null>(null);

  // 逐条存附件：每条记录各生成一份文件，写进它自己的附件字段
  const attachmentFields = useMemo(() => fieldMetas.filter((f) => f.type === 17), [fieldMetas]);
  const [saveField, setSaveField] = useState<string>(() => pickAttachmentField(context.tableId, attachmentFields));
  const [saveMode, setSaveMode] = useState<'append' | 'replace'>('replace');
  const [saveFormat, setSaveFormat] = useState<AttachmentFormat>('pdf');
  const fieldId = attachmentFields.some((f) => f.id === saveField)
    ? saveField
    : pickAttachmentField(context.tableId, attachmentFields);

  const run = async (action: 'print' | 'pdf' | 'attach') => {
    setBusy(action);
    setProgress('正在读取记录…');
    try {
      const ids = await getRecordIds(context.tableId, context.viewId, range);
      if (ids.length === 0) {
        notification.info({ message: '没有可打印的记录' });
        return;
      }
      const capped = ids.slice(0, MAX_BATCH);

      // 先把数据取齐再扣额度：中途某条记录取数失败就中止，额度不该白扣
      const dataList: ContractRenderData[] = [];
      const fetchedAt: number[] = [];
      for (let i = 0; i < capped.length; i++) {
        setProgress(`正在读取数据 ${i + 1}/${capped.length}…`);
        dataList.push(await fetchContractData(context.tableId, capped[i], template, fieldMetas, context.viewId));
        fetchedAt.push(Date.now());
      }
      if (!(await gate())) return;

      if (action === 'attach') {
        await saveEach(capped, dataList, fetchedAt);
        if (ids.length > MAX_BATCH) {
          notification.warning({ message: `记录较多，仅处理前 ${MAX_BATCH} 条`, description: `共 ${ids.length} 条` });
        }
        return;
      }

      const html = buildBatchHtml(template, dataList);
      if (action === 'print') {
        printHtmlViaIframe(html);
      } else {
        setProgress('正在生成 PDF…');
        const tableName = await getTableName(context.tableId).catch(() => '批量单据');
        const fileName = buildPdfFileName(`${tableName}_批量${capped.length}`);
        // 逐条渲染再追加。整批拼成一张长画布会超出浏览器画布高度上限，
        // 十几条以上就会导出一堆白页。
        const widthMm = paperWidthMm(template.settings);
        await exportManyToPdf({
          count: dataList.length,
          paperSize: template.settings.paperSize,
          orientation: template.settings.orientation,
          marginMm: template.settings.marginMm,
          fileName,
          onProgress: (done, total) => setProgress(`正在生成 PDF ${done}/${total}…`),
          next: async (i) =>
            renderHtmlOffscreen(buildBatchHtml(template, [dataList[i]]), widthMm),
        });
        notification.success({ message: `已导出 ${capped.length} 条`, description: fileName });
      }
      if (ids.length > MAX_BATCH) {
        notification.warning({ message: `记录较多，仅处理前 ${MAX_BATCH} 条`, description: `共 ${ids.length} 条` });
      }
      onClose();
    } catch (err) {
      notification.error({ message: '批量失败', description: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(null);
      setProgress('');
      setPct(null);
    }
  };

  /**
   * 一条一条生成、上传、写回。某条失败不中断整批 —— 跑到第 60 条断网，前 59 条
   * 已经存好了，只把失败的列出来让用户重跑那几条（选「勾选的记录」）。
   * 串行而不是并发：上传和写单元格都有频控，并发只会让失败变多。
   */
  const saveEach = async (ids: string[], dataList: ContractRenderData[], fetchedAt: number[]) => {
    const fieldName = attachmentFields.find((f) => f.id === fieldId)?.name ?? '';
    const custom = (template.settings.exportFileName || '').trim();
    const failed: string[] = [];
    setPct(0);
    for (let i = 0; i < ids.length; i++) {
      setProgress(`正在存入 ${i + 1}/${ids.length}…`);
      try {
        let data = dataList[i];
        if (Date.now() - fetchedAt[i] > ATTACHMENT_TTL_MS) {
          data = await fetchContractData(context.tableId, ids[i], template, fieldMetas, context.viewId);
        }
        const file = await buildAttachmentFile({
          html: buildContractHtml(template, data, 'output'), settings: template.settings,
          format: saveFormat, name: custom ? resolveFileNameTemplate(custom, data) : template.name,
        });
        await saveFileToAttachmentCell(context.tableId, fieldId, ids[i], file, saveMode);
      } catch (err) {
        failed.push(`第 ${i + 1} 条：${err instanceof Error ? err.message : String(err)}`);
      }
      setPct(Math.round(((i + 1) / ids.length) * 100));
    }
    const ok = ids.length - failed.length;
    if (ok > 0) rememberAttachmentField(context.tableId, fieldId);
    if (failed.length === 0) {
      notification.success({ message: `已存入 ${ok} 条`, description: `附件字段「${fieldName}」` });
      onClose();
    } else {
      notification.warning({
        message: `存入 ${ok} 条，失败 ${failed.length} 条`,
        description: (
          <div style={{ fontSize: 12, maxHeight: 160, overflowY: 'auto' }}>
            {failed.slice(0, 10).map((f) => (
              <div key={f}>{f}</div>
            ))}
            {failed.length > 10 && <div>…另有 {failed.length - 10} 条</div>}
            <div style={{ marginTop: 6 }}>勾选失败的记录，再选「勾选的记录」重跑即可。</div>
          </div>
        ),
        duration: 0,
      });
    }
  };

  return (
    <Modal open={open} onCancel={busy ? undefined : onClose} footer={null} title="批量打印 / 导出 / 存附件" width={380} destroyOnClose>
      <Space direction="vertical" size={14} style={{ width: '100%' }}>
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>记录范围</Typography.Text>
          <Radio.Group
            value={range}
            onChange={(e) => setRange(e.target.value)}
            style={{ display: 'block', marginTop: 6 }}
            options={[
              { label: '视图可见记录', value: 'visible' },
              { label: '勾选的记录', value: 'selected' },
              { label: '全表记录', value: 'all' },
            ]}
          />
        </div>
        <Alert
          type="info"
          showIcon
          message={`每条记录生成一页，最多 ${MAX_BATCH} 条。`}
        />
        {busy && (
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>{progress}</Typography.Text>
            {pct !== null && <Progress percent={pct} size="small" />}
          </div>
        )}
        <Space>
          <Button type="primary" icon={<PrinterOutlined />} loading={busy === 'print'} disabled={!!busy} onClick={() => void run('print')}>
            批量打印
          </Button>
          <Button icon={<FilePdfOutlined />} loading={busy === 'pdf'} disabled={!!busy} onClick={() => void run('pdf')}>
            批量导出 PDF
          </Button>
        </Space>

        {/* 逐条存附件：每条记录生成自己的文件，写回它自己的附件格子 */}
        <div style={{ borderTop: '1px solid var(--border, #eee)', paddingTop: 12 }}>
          <Typography.Text strong style={{ fontSize: 12.5 }}>逐条存入附件字段</Typography.Text>
          <Typography.Paragraph type="secondary" style={{ fontSize: 11.5, margin: '2px 0 8px' }}>
            每条记录各生成一份 {saveFormat === 'pdf' ? 'PDF' : 'Excel'}，存进这条记录自己的附件格子。
          </Typography.Paragraph>
          {attachmentFields.length === 0 ? (
            <Alert type="warning" showIcon message="这张表没有附件字段，先在表里加一个附件列" style={{ fontSize: 12 }} />
          ) : (
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Radio.Group
                size="small"
                value={saveFormat}
                onChange={(e) => setSaveFormat(e.target.value)}
                disabled={!!busy}
                optionType="button"
                options={[{ label: 'PDF', value: 'pdf' }, { label: 'Excel（.xlsx）', value: 'xlsx' }]}
              />
              {saveFormat === 'xlsx' && (
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  Excel 保留可编辑文字、表格和合并格。水印及复杂版式以 PDF 为准。
                </Typography.Text>
              )}
              <Select
                size="small"
                style={{ width: '100%' }}
                value={fieldId || undefined}
                onChange={setSaveField}
                disabled={!!busy}
                options={attachmentFields.map((f) => ({ label: f.name, value: f.id }))}
              />
              <Radio.Group
                size="small"
                value={saveMode}
                onChange={(e) => setSaveMode(e.target.value)}
                disabled={!!busy}
                optionType="button"
                options={[
                  { label: '替换原附件', value: 'replace' },
                  { label: '追加', value: 'append' },
                ]}
              />
              <Button
                icon={<PaperClipOutlined />}
                loading={busy === 'attach'}
                disabled={!!busy || !fieldId}
                onClick={() => void run('attach')}
              >
                逐条存入附件
              </Button>
            </Space>
          )}
        </div>
      </Space>
    </Modal>
  );
}
